import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejectedWithValue } from "@reduxjs/toolkit";
import { fetchData } from "../utils";
import * as endpoints from '../settings';
import { schemas } from './schemas';
import { setNotification } from "./notificationsSlice";
import { has, omit, toLower, toString, toUpper, size, pick, find } from "lodash";
import { activateContract, updateContractsDate } from "./api/contracts";
import { addOrgLicense, editOrgLicense } from "./api/organisations";
import { getUserSavedFilters } from "./api/users";
import { getIssueAttachments, getIssueComments } from "./api/issues";
import { addPackageDocumetationLink, editPackageDocumetationLink, getFuturePackages, getMasterKitList, getPackageDocumetationLinks, getPackagesByIds, getRecentPackages, updatePackages, verifyPackage } from "./api/packages";
import { getAuditPackageDetails, getFullPackageAudit } from "./api/audit";
import { getUploadDetails } from "./api/uploads";
import { registerPackage, setFileUploaded, uploadContracts, uploadShcFile } from "./uploadSlice";
import { addItem } from "./formSlice";


const dataChunkUrls = {
    issues: { url: endpoints.API_SUPPORT_ISSUES },
    contracts: {
        url: endpoints.API_CONTRACTS_ALL,
        all: {
            url: endpoints.API_ORGS_CONTRACTS
        },
        organisations: {
            url: endpoints.API_CONTRACT_ORGS
        },
        packages: {
            url: endpoints.API_CONTRACT_PACKAGES
        }
     },
    users: { url: endpoints.API_USERS },
    packages: { url: endpoints.API_PACKAGES },
    licenses: { url: endpoints.API_LICENSES },
    organisations: {
        url: endpoints.API_ORGS_ALL,
        users: {
            url: endpoints.API_ORG_DETAILS + "/users",
            schema: schemas.ORG_USERS
        },
        issues: {
            url: endpoints.API_ORG_DETAILS + "/issues",
            schema: schemas.ORG_ISSUES
        },
        licenses: {
            url: endpoints.API_ORG_DETAILS + "/licenses",
            schema: schemas.ORG_LICENSES
        }
    },
    audit: {
        downloads: {
            url: endpoints.API_AUDIT_PACKAGES_DOWNLOAD
        },
        users: {
            url: endpoints.API_AUDIT_USERS
        },
        grants: {
            url: endpoints.API_AUDIT_USER_GRANTS
        },
        impersonations: {
            url: endpoints.API_AUDIT_IMPERSONATIONS
        },
        contracts: {
            url: endpoints.API_AUDIT_ORG_CONTRACTS
        },
        packages: {
            url: endpoints.API_AUDIT_PACKAGES
        },
        orgs: {
            url: endpoints.API_AUDIT_ORGS
        },
        permissions: {
            url: endpoints.API_AUDIT_PACKAGES + "/permissions"
        }
    },
    services: {
        shc: {
            url: endpoints.SHC_UPLOADS
        }
    }
};

export const getDataChunk = createAsyncThunk(
    "getDataChunk",
    async({ path, org, selected, search }, { dispatch, rejectWithValue, signal }) => {
        try {
            let url, page, tab, headers = { "Content-Type": "application/json" }, schema;
            if (Array.isArray(path) && path.length) {
                tab = path[0];
                page = path[1];
                url = page ? dataChunkUrls[page][tab].url : dataChunkUrls[tab].url;
                schema = page ? dataChunkUrls[page][tab].schema || {} : schemas[tab] || {};
            }
            if (typeof url === "function") {
                url = url(selected);
            }
            if (org) headers["X-Org-Alias"] = toLower(org);
            if (url) {
                const response = await fetchData(url + search, {
                    signal: signal,
                    headers: headers
                }, schema);
                if (Object.keys(schema).length) {
                    let data = {};
                    if (response && response.entities && response.entities[tab]) {
                        data = Object.values(response.entities[tab])[0];
                    }
                    return { total: data.total || 0, result: data.result || [], details: omit(data, ["total", "result"]) }
                } else {
                    return Object.assign(response.result, { details: omit(response.result, ["total", "result"])})
                }
            }
        } catch (e) {
            if (e.code != 20) {
                dispatch(setNotification(e));
            }
            return rejectWithValue({ code: e.code });
        };
        return rejectWithValue({});
    }
);

const detailsSchemas = {
    organisations: {
        url: endpoints.API_ORG_DETAILS,
        schema: schemas.ORG,
        users: {
            url: endpoints.API_ORG_DETAILS + "/users",
            schema: schemas.ORG_USERS
        },
        issues: {
            url: endpoints.API_ORG_DETAILS + "/issues",
            schema: schemas.ORG_ISSUES
        }
    },
    users: {
        url: endpoints.API_USER_DETAILS,
        schema: schemas.USER
    },
    issues: {
        url: endpoints.API_SUPPORT_ISSUE,
        schema: schemas.ISSUE
    },
    contracts: {
        url: (id) => endpoints.API_CONTRACT_DETAILS(id),
        schema: schemas.CONTRACT
    },
    packages: {
        url: endpoints.API_PACKAGE,
        schema: schemas.PACKAGE
    },
    audit: {
        downloads: {
            url: endpoints.API_AUDIT_PACKAGES_DOWNLOAD + "/details",
            schema: schemas.AUDIT_DETAILS
        },
        users: {
            url: endpoints.API_AUDIT_USERS + "/user",
            schema: schemas.AUDIT_DETAILS
        },
        grants: {
            url: endpoints.API_AUDIT_USER_GRANTS + "/grant",
            schema: schemas.AUDIT_DETAILS
        },
        impersonations: {
            url: endpoints.API_AUDIT_IMPERSONATIONS + "/impersonation",
            schema: schemas.AUDIT_DETAILS
        },
        contracts: {
            url: endpoints.API_AUDIT_ORG_CONTRACTS + "/contract",
            schema: schemas.AUDIT_DETAILS
        },
        orgs: {
            url: (id) => endpoints.API_AUDIT_ORGS + "/" + id,
            schema: schemas.AUDIT_DETAILS
        },
        permissions: {
            url: (id) => endpoints.API_AUDIT_PACKAGES + "/permissions/" + id,
            schema: schemas.AUDIT_DETAILS
        }
    },
    licenses: {
        url: (id) => endpoints.API_ORG_DETAILS + "/licenses/" + id,
        schema: schemas.LISENSE
    },
    shc: {
        url: endpoints.SHC_UPLOAD,
        schema: schemas.SHC_UPLOAD
    }
};

export const getDetails = createAsyncThunk(
    "getDetails",
    async({ page, org, tab, selected }, { dispatch, rejectWithValue, signal }) => {
        try {
            let url = null, headers = {}, schema = {};
            if (Object.keys(detailsSchemas).indexOf(page) > -1) {
                if (tab) {
                    if (Object.keys(detailsSchemas[page]).indexOf(tab) > -1) {
                        url = detailsSchemas[page][tab].url;
                        schema = detailsSchemas[page][tab].schema;
                    };
                } else {
                    url = detailsSchemas[page].url;
                    schema = detailsSchemas[page].schema;
                };
                if (typeof url === "function") {
                    url = url(selected);
                }
                if (org) headers["X-Org-Alias"] = toLower(org);
                if (page === "users") headers["X-User-Email"] = toLower(selected);
                if (page === "issues") headers["X-Ticket-Ref"] = toUpper(selected);
                if (page === "packages") headers["X-Pkg-Id"] = selected;
                if (page === "audit") headers["X-Audit-Number"] = selected;
                if (page === "shc") headers["X-Upload-Ref"] = toLower(selected);
            };
            if (url) {
                const response = await fetchData(url, {
                    signal: signal,
                    headers: headers
                }, schema);
                if (response && selected) {
                    if (page === "issues" || tab === "issues") {
                        dispatch(getIssueComments(selected));
                        dispatch(getIssueAttachments(selected));
                    }
                    if (page === "packages") {
                        dispatch(getPackageDocumetationLinks(selected));
                    }
                }
                if (response && response.entities && response.entities[page] && response.entities[page][toLower(toString(selected))]) {
                    return(response.entities[page][toLower(selected)]);
                }
            };
        } catch (e) {
            if (e.code != 20) {
                dispatch(setNotification(e));
            }
            return rejectWithValue({ code: e.code });
        };
        return rejectWithValue({});
    }
);


const initialState = {
    data: [],
    details: {},
    loading: false,
    notFound: false,
    forbidden: false,
    tab: null,
    total: 0,
    shouldUpdate: false,
    filters: {},
    selectedItems: null,
    errors: {}
};

const isAPendingDataAction = isPending(getDetails, getMasterKitList, getPackagesByIds);

const isAPendingAction = isPending(getDataChunk, getIssueComments, getIssueAttachments, getUploadDetails, getAuditPackageDetails, getRecentPackages, getFuturePackages);

const isAFulfilledAction = isFulfilled(getDataChunk, getDetails, getIssueComments, getIssueAttachments, getUploadDetails, getAuditPackageDetails, getPackagesByIds, getMasterKitList, getRecentPackages, getFuturePackages);

const isARejectedWithValueAction = isRejectedWithValue(getDataChunk, getDetails, getIssueComments, getIssueAttachments, getUploadDetails, getAuditPackageDetails, getMasterKitList);

const isAFulfilledActionDetails = isFulfilled(getDetails, getUploadDetails, getAuditPackageDetails, getPackagesByIds, getMasterKitList);

const shouldUpdateData = isFulfilled(addOrgLicense, editOrgLicense, addItem, uploadShcFile, uploadContracts, registerPackage, verifyPackage, updateContractsDate,
    activateContract, setFileUploaded, updatePackages);

const dataSlice = createSlice({
    name: 'data',
    initialState,
    reducers: {
        resetData: () => {
            return initialState;
        },
        setSelectionMode: (state) => {
            state.selectedItems = state.selectedItems === null ? {} : null;
        },
        selectToDownload: (state, { payload }) => {
            const { selectedItems } = state;
            let selected = typeof selectedItems == "object" && typeof selectedItems !== null ? selectedItems : {};
            if (selected[payload.key]) {
                delete selected[payload.key]
            } else {
                selected[payload.key] = pick(payload, ["key", "name", "size", "minos", "files"]);
            };
            state.selectedItems = selected;
        },
        setAsDownloaded: (state, { payload }) => {
            let pkg = find(state.data, d => d && Array.isArray(d.files) ? d.files.includes(payload) : false);
            if (pkg && pkg.downloaded == false) {
                pkg.downloaded = true;
            }
        },
        setErrors: (state, { payload }) => {
            state.errors = payload;
        }
    },
    extraReducers: (builder) => {
        builder
        .addCase(getDataChunk.fulfilled, (state, { payload }) => {
            state.data = payload.result || [];
            state.total = payload.total || 0;
            state.details = payload.details || {};
            state.notFound = payload.code === 404;
            state.forbidden = payload.code === 403;
            let maxPage = Math.ceil(payload.total / endpoints.PAGE_SIZE);
            if (maxPage > 0 && maxPage < state.currentPage) {
                state.currentPage = maxPage;
            }
            state.shouldUpdate = false;
        })
        .addCase(getIssueComments.pending, (state) => {
            state.details["comments"] = null;
        })
        .addCase(getIssueComments.fulfilled, (state, { payload }) => {
            state.details["comments"] = payload;
        })
        .addCase(getIssueComments.rejected, (state) => {
            state.details["comments"] = [];
        })
        .addCase(getIssueAttachments.pending, (state) => {
            state.details["attachments"] = null;
        })
        .addCase(getIssueAttachments.fulfilled, (state, { payload }) => {
            state.details["attachments"] = payload;
        })
        .addCase(getIssueAttachments.rejected, (state) => {
            state.details["attachments"] = [];
        })
        .addCase(getPackageDocumetationLinks.fulfilled, (state, { payload }) => {
            state.details["docs"] = payload;
        })
        .addCase(addPackageDocumetationLink.fulfilled, (state, { payload }) => {
            let docs = Array.isArray(state.details.docs) ? state.details.docs : [];
            docs.push(payload);
            state.details["docs"] = docs;
        })
        .addCase(editPackageDocumetationLink.fulfilled, (state, { payload }) => {
            state.details["docs"] = state.details["docs"].map(doc => doc.id === payload.id ? payload : doc);
        })
        .addCase(getFullPackageAudit.fulfilled, (state, { payload }) => {
            if (has(state.details, "packageNumber") && state.details.packageNumber == payload.packageNumber || has(state.details, "packageName") && state.details.packageName == payload.packageName) {
                state.details.fullAudit = payload.result;
            }
        })
        .addCase(getFullPackageAudit.rejected, (state, { payload }) => {
            if (has(state.details, "packageNumber") && state.details.packageNumber == payload.packageNumber || has(state.details, "packageName") && state.details.packageName == payload.packageName) {
                state.details.fullAudit = [];
            }
        })
        .addCase(getUserSavedFilters.fulfilled, (state, { payload }) => {
            state.filters = payload;
        })
        .addCase(getRecentPackages.fulfilled, (state, { payload}) => {
            state.details["recent"] = payload;
        })
        .addCase(getRecentPackages.rejected, (state) => {
            state.details["recent"] = {};
        })
        .addCase(getFuturePackages.fulfilled, (state, { payload }) => {
            state.details["future"] = payload;
        })
        .addCase(getFuturePackages.rejected, (state) => {
            state.details["future"] = {};
        })
        .addMatcher(
            action => isAPendingDataAction(action),
            (state => ({
                ...initialState,
                loading: true
            }))
        )
        .addMatcher(
            action => isAFulfilledActionDetails(action),
            (state, { payload }) => {
                state.details = payload;
                state.data = [];
                state.total = 0;
                state.notFound = payload.code === 404;
                state.forbidden = payload.code === 403;
                state.shouldUpdate = false;
            }
        )
        .addMatcher(
            action => isAPendingAction(action),
            (state) => {
                state.loading = true;
            }
        )
        .addMatcher(
            action => isAFulfilledAction(action),
            (state) => {
                state.loading = false;
            }
        )
        .addMatcher(
            action => isARejectedWithValueAction(action),
            (state, { payload }) => ({
                ...initialState,
                sortColumn: state.sortColumn,
                sortOrder: state.sortOrder,
                loading: false,
                notFound: payload.code === 404,
                forbidden: payload.code === 403
            })
        )
        .addMatcher(
            action => shouldUpdateData(action),
            (state, action) => {
                if (has(action.payload, "entity")) {
                    let { entity } = action.payload;
                    if (entity == "grants") {
                        entity = "users";
                    } else if (entity == "restrictions" || entity == "prereq") {
                        entity = "packages";
                    } else if (entity == "contract") {
                        entity = "contracts";
                    }
                    state.shouldUpdate = entity;
                }
            }
        )
    }
});

export const { resetData, selectToDownload, setSelectionMode, setAsDownloaded, setErrors } = dataSlice.actions;
export default dataSlice.reducer;